import { faMinus } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Calendar } from 'primereact/calendar';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';

import useHaveValuesChanged from '../../../../hooks/useHaveValuesChanged';
import useSearchQueryDateParam from '../../../../hooks/useSearchQueryDateParam';
import useSearchQueryDropdownParam from '../../../../hooks/useSearchQueryDropdownParam';
import useSearchQueryMultiSelectParam from '../../../../hooks/useSearchQueryMultiSelectParam';
import useSearchQueryParam from '../../../../hooks/useSearchQueryParam';
import { debounceTimeout } from '../../../../utils/constants/misc';
import { httpDateFormat } from '../../../../utils/helpers/formatting';
import { pipeMultiSelectParam } from '../../../../utils/helpers/http';
import { renderIcon } from '../../../../utils/helpers/icon';
import {
  getSearchQueryParam,
  tryDateSearchParam,
  tryMultiSelectParam,
} from '../../../../utils/helpers/searchQuery';
import { TableProps } from '../../../DataTable/Table/Table';
import FiltersCounter from '../../Components/Filters/FiltersCounter';
import {
  IncompleteOrderStatus,
  incompleteOrderStatusOptions,
} from './Incomplete.functions';

const DEFAULT_CHECKED_STATUSES = [
  IncompleteOrderStatus.CREATED,
  IncompleteOrderStatus.NOTIFICATION_SENT,
  IncompleteOrderStatus.NOTIFICATION_DUPLICATE,
];

function useTableFilters(
  page: number,
  setPage: TableProps['setPage'],
  limit: number
) {
  const { t } = useTranslation();
  const location = useLocation();
  const [dateFrom, setDateFrom] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'createdAtFrom') ?? ''
      ) ?? dayjs().toDate()
  );
  const [dateTo, setDateTo] = useState<Date | null>(
    () =>
      tryDateSearchParam(
        getSearchQueryParam(location.search, 'createdAtTo') ?? ''
      ) ?? dayjs().toDate()
  );
  const [orderSerialNumber, setOrderSerialNumber] = useState<string>(
    () => getSearchQueryParam(location.search, 'orderSerialNumber') ?? ''
  );
  const [ordererName, setOrdererName] = useState<string>(
    () => getSearchQueryParam(location.search, 'ordererName') ?? ''
  );
  const [senderName, setSenderName] = useState<string>(
    () => getSearchQueryParam(location.search, 'senderName') ?? ''
  );
  const [recipientName, setRecipientName] = useState<string>(
    () => getSearchQueryParam(location.search, 'recipientName') ?? ''
  );
  const [phoneNumber, setPhoneNumber] = useState<string>(
    () => getSearchQueryParam(location.search, 'phoneNumber') ?? ''
  );
  const [rawAddress, setRawAddress] = useState<string>(
    () => getSearchQueryParam(location.search, 'rawAddress') ?? ''
  );
  const [status, setStatus] = useState(() => {
    const value = getSearchQueryParam(location.search, 'status') ?? null;

    if (!value) return DEFAULT_CHECKED_STATUSES;

    return tryMultiSelectParam(value) ?? null;
  });
  const [addressType, setAddressType] = useState<string | null>(
    () => getSearchQueryParam(location.search, 'addressType') ?? null
  );

  useEffect(() => {
    if (!setPage) {
      return;
    }
    setPage(1);
  }, [dateFrom, dateTo, setPage]);

  const addressTypeOptions = useMemo(
    () => [
      {
        value: 'pickup',
        label: t('Dispatch address'),
      },
      { value: 'delivery', label: t('Delivery address') },
    ],
    [t]
  );

  const debouncedSerialNumber = useDebounce(orderSerialNumber, debounceTimeout);
  const debouncedOrdererName = useDebounce(ordererName, debounceTimeout);
  const debouncedSenderName = useDebounce(senderName, debounceTimeout);
  const debouncedRecipientName = useDebounce(recipientName, debounceTimeout);
  const debouncedRawAddress = useDebounce(rawAddress, debounceTimeout);
  const debouncedPhoneNumber = useDebounce(phoneNumber, debounceTimeout);

  const filtersArr = useMemo(
    () => [
      dateFrom,
      dateTo,
      debouncedSerialNumber,
      debouncedOrdererName,
      debouncedSenderName,
      debouncedRecipientName,
      debouncedPhoneNumber,
      status,
      addressType,
      debouncedRawAddress,
    ],
    [
      dateFrom,
      dateTo,
      debouncedSerialNumber,
      debouncedOrdererName,
      debouncedSenderName,
      debouncedRecipientName,
      debouncedPhoneNumber,
      status,
      addressType,
      debouncedRawAddress,
    ]
  );

  const haveFiltersChanged = useHaveValuesChanged(filtersArr);

  const httpFiltersObj = useMemo(() => {
    const _dateFrom = dayjs(dateFrom as any);
    const _dateTo = dayjs(dateTo as any);

    return {
      createdAtFrom: httpDateFormat(_dateFrom),
      createdAtTo: httpDateFormat(_dateTo),
      orderSerialNumber: debouncedSerialNumber,
      ordererName: debouncedOrdererName,
      senderName: debouncedSenderName,
      recipientName: debouncedRecipientName,
      phoneNumber: debouncedPhoneNumber,
      rawAddress: debouncedRawAddress,
      status: pipeMultiSelectParam(status),
      addressType,
      page: haveFiltersChanged ? 1 : page,
      limit,
      summary: 1,
    };
  }, [
    dateFrom,
    dateTo,
    status,
    addressType,
    debouncedSerialNumber,
    debouncedOrdererName,
    debouncedSenderName,
    debouncedRecipientName,
    debouncedPhoneNumber,
    debouncedRawAddress,
    haveFiltersChanged,
    page,
    limit,
  ]);

  useSearchQueryDateParam('createdAtFrom', dateFrom);
  useSearchQueryDateParam('createdAtTo', dateTo);
  useSearchQueryParam('orderSerialNumber', debouncedSerialNumber);
  useSearchQueryParam('ordererName', debouncedOrdererName);
  useSearchQueryParam('senderName', debouncedSenderName);
  useSearchQueryParam('recipientName', debouncedRecipientName);
  useSearchQueryParam('phoneNumber', debouncedPhoneNumber);
  useSearchQueryParam('rawAddress', debouncedRawAddress);
  useSearchQueryMultiSelectParam('status', status);
  useSearchQueryDropdownParam(
    'addressType',
    addressType,
    setAddressType,
    addressTypeOptions
  );

  const basicFiltersActiveFilterCount = Object.values({
    dateFrom, // date_from and date_to is count like one
    orderSerialNumber,
    ordererName,
    senderName,
    recipientName,
    phoneNumber,
    rawAddress,
    status,
    addressType,
  }).filter(Boolean).length;

  const filters = useMemo<JSX.Element>(
    () => (
      <Accordion multiple activeIndex={[0]}>
        <AccordionTab
          disabled
          headerTemplate={
            <FiltersCounter
              description={t('Basic filters')}
              counter={basicFiltersActiveFilterCount}
            />
          }
        >
          <div className="sidebar_filter">
            <label htmlFor="filter_date_from">{t('Created Date')}</label>
            <div className="sidebar_filter_row">
              <Calendar
                id="filter_date_from"
                value={dateFrom ?? undefined}
                monthNavigator
                yearNavigator
                dateFormat="dd/mm/yy"
                yearRange={`2009:${dayjs().format('YYYY')}`}
                maxDate={new Date()}
                onChange={(e) => setDateFrom(e.value as Date | null)}
                inputClassName={classNames({
                  sidebar_filter_active: dateFrom,
                })}
              />
              <span style={{ margin: 'auto' }}>{renderIcon(faMinus)}</span>
              <Calendar
                id="filter_date_to"
                value={dateTo ?? undefined}
                onChange={(e) => {
                  setDateTo(e.value as Date | null);
                }}
                dateFormat="dd/mm/yy"
                maxDate={new Date()}
                monthNavigator
                yearNavigator
                yearRange={`2009:${dayjs().format('YYYY')}`}
                inputClassName={classNames({
                  sidebar_filter_active: dateTo,
                })}
              />
            </div>
          </div>
          <div className="sidebar_filter">
            <label htmlFor="orderSerialNumber">{t('Serial Number')}</label>
            <InputText
              id="orderSerialNumber"
              value={orderSerialNumber ?? ''}
              onChange={(e) => setOrderSerialNumber(e.target.value)}
              className={classNames({
                sidebar_filter_active: orderSerialNumber,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="ordererName">{t('Orderer')}</label>
            <InputText
              id="ordererName"
              value={ordererName ?? ''}
              onChange={(e) => setOrdererName(e.target.value)}
              className={classNames({
                sidebar_filter_active: ordererName,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="orderSerialNumber">{t('Sender Name')}</label>
            <InputText
              id="senderName"
              value={senderName ?? ''}
              onChange={(e) => setSenderName(e.target.value)}
              className={classNames({
                sidebar_filter_active: senderName,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="orderSerialNumber">{t('Recipient Name')}</label>
            <InputText
              id="recipientName"
              value={recipientName ?? ''}
              onChange={(e) => setRecipientName(e.target.value)}
              className={classNames({
                sidebar_filter_active: recipientName,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="phoneNumber">{t('Phone')}</label>
            <InputText
              id="phoneNumber"
              value={phoneNumber ?? ''}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={classNames({
                sidebar_filter_active: phoneNumber,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="rawAddress">{t('Address')}</label>
            <InputText
              id="rawAddress"
              value={rawAddress ?? ''}
              onChange={(e) => setRawAddress(e.target.value)}
              className={classNames({
                sidebar_filter_active: rawAddress,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="status_filter">{t('Address type')}</label>
            <Dropdown
              id="status_filter"
              onChange={(e: DropdownChangeParams) => setAddressType(e.value)}
              options={addressTypeOptions}
              value={addressType}
              showClear
              filter
              placeholder={t('All')}
              className={classNames({
                sidebar_filter_active: addressType,
              })}
            />
          </div>
          <div className="sidebar_filter">
            <label htmlFor="status_filter">{t('Status')}</label>
            <MultiSelect
              inputId="filter_status"
              value={status}
              options={incompleteOrderStatusOptions}
              maxSelectedLabels={0}
              selectedItemsLabel={t('{0} selected')}
              filter
              showClear
              onChange={(e: DropdownChangeParams) => setStatus(e.target.value)}
              // placeholder={t('All')}
              className={classNames({
                sidebar_filter_active: status,
              })}
            />
          </div>
        </AccordionTab>
      </Accordion>
    ),
    [
      dateFrom,
      dateTo,
      orderSerialNumber,
      ordererName,
      senderName,
      recipientName,
      phoneNumber,
      rawAddress,
      status,
      addressType,
      addressTypeOptions,
      basicFiltersActiveFilterCount,
      t,
    ]
  );

  const headerFiltersCount = useMemo(
    () => basicFiltersActiveFilterCount,
    [basicFiltersActiveFilterCount]
  );

  function resetAllFilters() {
    setDateFrom(new Date());
    setDateTo(new Date());
    setOrderSerialNumber('');
    setOrdererName('');
    setSenderName('');
    setRecipientName('');
    setPhoneNumber('');
    setRawAddress('');
    setAddressType('');
    setStatus(null);
  }

  return {
    filters,
    resetAllFilters,
    httpFiltersObj,
    headerFiltersCount,
  };
}

export default useTableFilters;
